import * as yup from 'yup'
import { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'
import { useParams, useSearchParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material'
import { useFormik } from 'formik'
import DialogBox from '../../components/DialogBox'
import Timeline from './Timeline'
import Loading from '../../../../components/Loading'
import FormErrorsModal from '../../../../components/FormErrorsModal'
import SuccessModal from '../../../../components/SuccessModal'
import MultiImageEdit from '../../../../components/MultiImageEdit'
import axiosInstance from '../../../../utils/axiosInstance'
import atsProviders from '../../../../config/atsProviders'
import classes from '../style.module.scss'
import { leadStatuses } from '../../../../constants'
import { IdataSourceItem, IfileItem } from '../../../../types/other'
import { fixImgSrc, hasPermission } from '../../../../utils/tools'
import { TgraphQlFile } from '../../../../types'
import { useDispatch, useSelector } from '../../../../store/store'
import { dialogToggle } from '../../../../store/slices/dialog'
import { setTrigger } from '../../../../store/slices/lead'
import { classifyLeadField, firstFieldValue, parseFieldData } from '../../../../utils/leadFields'
import AtsPushStatusNote, { useAtsPushState } from '../../components/AtsPushStatusNote'
import useApplicantPrivacy from '../../../../hooks/useApplicantPrivacy'

const ReadEditLead = () => {
  const intl = useIntl()
  const { open } = useSelector((state) => state.dialog)
  const [title, setTitle] = useState('Details')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [questions, setQuestions] = useState([])
  const [formatQuestion, setFormatQuestion] = useState({})
  const [files, setFiles] = useState<IfileItem[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const { id: campaignId } = useParams()
  const id = searchParams.get('id') || null
  const isUpdate = !!id
  const dispatch = useDispatch()
  const [idsToDelete, setIdsToDelete] = useState<string[]>([])
  const [idsToCreate, setIdsToCreate] = useState<string[]>([])
  const [dataSources, setDataSources] = useState<IdataSourceItem[]>([])

  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [status, setStatus] = useState('')
  const [platform, setPlatform] = useState('')
  const [campaignData, setCampaignData] = useState({
    id: '',
    campaignTitle: '',
    questions: '',
  })

  const { permissions } = useSelector((state) => state.user)
  const { role } = useSelector((state) => state.user.userInfo)
  const permission = hasPermission(permissions?.leadsAndTalentsDetailsFields)
  const privacy = useApplicantPrivacy()
  // Masking applies to an existing lead only: on create the user is typing the values in.
  // The update form never submits name, phone or email, so the masked fields are read-only.
  const isMasked = isUpdate && privacy.isMasked
  const shownPhone = isMasked ? privacy.maskPhone(phoneNumber) : phoneNumber
  const shownEmail = isMasked ? privacy.maskEmail(email) : email
  // 'Details' is the placeholder title shown until the lead's name is known.
  const shownTitle = isMasked && title !== 'Details' ? privacy.maskName(title) : title

  const [isSendingToTalentBridge, setIsSendingToTalentBridge] = useState(false)
  const [talentBridgeSuccessModalOpen, setTalentBridgeSuccessModalOpen] = useState(false)
  const [, setTalentBridgeResponse] = useState(null)
  const [atsCapabilityDisabled, setAtsCapabilityDisabled] = useState(false)

  const removeParams = useCallback(() => {
    setSearchParams((params) => {
      params.delete('id')
      return params
    })
  }, [setSearchParams])

  const handleCloseCB = useCallback(() => {
    dispatch(dialogToggle(false))
    removeParams()
  }, [dispatch, removeParams])

  const handleCloseSuccess = useCallback(() => {
    setSuccessModalOpen(false)

    removeParams()
    dispatch(dialogToggle(false))
  }, [dispatch, removeParams])

  const {
    data: fetchedLeadDetails,
    isLoading: loadingLeadDetails,
    mutate: refetchLeadDetails,
  } = useSWR(id ? `/lead/${id}` : null, (url) => axiosInstance.get(url).then((res) => res.data))

  // Same audience as the push button (renderAtsButton). Keeps the ATS note current while
  // the ATS confirms a push; a rejected push reloads the lead, whose status went back to new.
  const showAtsNote = isUpdate && role === 'admin'
  const { atsPush, confirmedNow } = useAtsPushState(
    id,
    fetchedLeadDetails,
    open && showAtsNote,
    (fresh) => {
      if (fresh.atsPushStatus === 'failed') refetchLeadDetails()
    }
  )

  // Loading states for mutations
  const [loadingMutation, setLoadingMutation] = useState(false)
  const [loadingCreateLead, setLoadingCreateLead] = useState(false)
  const [mutationError, setMutationError] = useState<any>(null)
  const [mutationErrorCreate, setMutationErrorCreate] = useState<any>(null)
  const [mutationErrorConnectCampaignWithLead, setMutationErrorConnectCampaignWithLead] =
    useState<any>(null)

  // Mutation functions using server routes
  const updateLead = async (data: any, where: any) => {
    try {
      setLoadingMutation(true)
      const response = await axiosInstance.put(`/lead/${where.id}/update`, data)
      return response.data
    } catch (error) {
      setMutationError(error)
      throw error
    } finally {
      setLoadingMutation(false)
    }
  }

  const createLeadMutation = async (data: any) => {
    try {
      setLoadingCreateLead(true)
      const response = await axiosInstance.post('/lead/create', data)
      return response.data
    } catch (error) {
      setMutationErrorCreate(error)
      throw error
    } finally {
      setLoadingCreateLead(false)
    }
  }

  const connectCampaignWithLeadMutation = async (campaignId: string, leadId: string) => {
    try {
      const response = await axiosInstance.post('/lead/connect-campaign', { campaignId, leadId })
      return response.data
    } catch (error) {
      setMutationErrorConnectCampaignWithLead(error)
      throw error
    }
  }

  const deleteLeadFileMutation = async (fileId: string) => {
    try {
      const response = await axiosInstance.delete(`/lead/file/${fileId}`)
      return response.data
    } catch (error) {
      console.error('Delete failed:', error)
      throw new Error('Deleting file failed')
    }
  }

  const validationSchema = yup.object({
    status: yup
      .string()
      .required()
      .oneOf(leadStatuses.map((i) => i.value)),
    question: yup.string().nullable(),
    metaNotes: yup.string().nullable(),
    metaLeadFiles: yup.object().nullable(),
  })

  const formik = useFormik({
    initialValues: {
      status: status || fetchedLeadDetails?.metaLeads?.[0]?.status || fetchedLeadDetails?.status,
      metaNotes: fetchedLeadDetails?.metaLeads?.[0]?.metaNotes || fetchedLeadDetails?.notes,
      metaLeadFiles: null,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const newFormData = {
        status: values.status,
        metaNotes: values.metaNotes,
        metaLeadFiles: values.metaLeadFiles,
      }
      const res = await updateLead(newFormData, { id })
      if (res?.updateMetaLead?.status) {
        // addChangeLogToTaskHistory(
        //   createOrderHistory,
        //   fetchedTaskDetails.task,
        //   res.data.updateTask,
        //   userInfo.id
        // )
        if (idsToDelete?.length) {
          await Promise.all(idsToDelete.map((id) => deleteLeadFileMutation(id)))
        }

        dispatch(dialogToggle(false))
        dispatch(setTrigger(true))
        setSuccessModalOpen(true)
      }
    },
  })

  const formikCreate = useFormik({
    initialValues: {
      status,
      metaNotes: fetchedLeadDetails?.metaLeads?.[0]?.metaNotes || fetchedLeadDetails?.notes,
      metaLeadFiles: null,
      campaign: { connect: { id: campaignId } },
      platform,
      createdTime: '',
      fieldData: [
        { name: 'phone_number', values: [phoneNumber] },
        { name: 'email', values: [email] },
        { name: 'full_name', values: [fullName] },
        { name: 'phone_number', values: [phoneNumber] },
        ...Object.entries(formatQuestion).map((item) => ({ name: item[0], values: [item[1]] })),
      ],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const newFormData = {
        campaign: values.campaign,
        status: values.status,
        fieldData: JSON.stringify(values.fieldData),
        platform: values.platform,
        createdTime: new Date().toISOString(),
        metaLeadFiles: values.metaLeadFiles,
      }

      const res = await createLeadMutation(newFormData)
      const leadId = res.createMetaLead.id

      if (campaignId) {
        await connectCampaignWithLeadMutation(campaignId, leadId)
      }
      if (res) {
        dispatch(setTrigger(true))
        setSuccessModalOpen(true)
      }
    },
  })

  async function handleSubmit() {
    if (isUpdate) {
      const errors = Object.values(formik.errors)
      if (errors?.length) {
        setErrorsModalOpen(true)
        setFormikErrors(errors as string[])
        return
      }
      if (files) {
        await formik.setFieldValue('metaLeadFiles', {
          create: files.map((i: IfileItem) => ({
            name: i.file.name,
            file: {
              upload: i.file,
            },
          })),
        })
      }

      return formik.handleSubmit()
    }

    const errors = Object.values(formikCreate.errors)
    if (errors?.length) {
      setErrorsModalOpen(true)
      setFormikErrors(errors as string[])
      return
    }
    if (files) {
      await formikCreate.setFieldValue('metaLeadFiles', {
        create: files.map((i: IfileItem) => ({
          name: i.file.name,
          file: {
            upload: i.file,
          },
        })),
      })
    }

    formikCreate.handleSubmit()
  }

  const RenderQuestions = useCallback(() => {
    // useCallback
    const formatQuestion = Object.entries(questions).map((question) => ({
      name: question[0].split('_').join(' '),
      value: question[1],
    }))

    const campaignQuestion = JSON.parse(campaignData?.questions || '[]')
      .filter((item) => item.type === 'CUSTOM')
      .map(({ id, key, label }) => ({ id, key, name: label, value: '' }))

    return (
      <>
        {(isUpdate ? formatQuestion.reverse() : campaignQuestion)?.map(
          ({ name, value = '', key = '' }) => (
            <Grid item xs={12} key={name}>
              <Tooltip
                title={`${name}: ${value || formatQuestion[key]}`}
                placement="top"
                enterDelay={500}
              >
                <TextField
                  label={name}
                  value={value || formatQuestion[key]} // isUpdate ? value : questionState.value
                  name={key}
                  variant="outlined"
                  fullWidth
                  onChange={(e) => {
                    setFormatQuestion((old) => ({ ...old, [e.target.name]: e.target.value }))
                  }}
                  multiline={value?.length > 55}
                  InputLabelProps={{
                    style: {
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'unset',
                      maxWidth: '100%',
                    },
                  }}
                  inputProps={{
                    style: {
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    },
                  }}
                />
              </Tooltip>
            </Grid>
          )
        )}
      </>
    )
  }, [campaignData.questions, isUpdate, questions])

  const generalPermission = permissions?.leadsAndTalents
  generalPermission && null

  const handleSendToTalentBridge = async () => {
    if (!id) return

    setIsSendingToTalentBridge(true)
    try {
      // Get job reference number from lead's campaign
      const response = await axiosInstance.post('/send-lead-to-talentbridge', {
        leadId: id,
      })

      setTalentBridgeResponse(response.data.talentBridgeData)
      setTalentBridgeSuccessModalOpen(true)

      // Update local form state
      setStatus('synced')
      formik.setFieldValue('status', 'synced')

      // Refresh the lead details to get updated status
      refetchLeadDetails()

      // Trigger global lead list refresh
      dispatch(setTrigger(true))
    } catch (error: any) {
      console.error('Error sending to TalentBridge:', error)
      const responseData = error?.response?.data
      const detailMessages = responseData?.details?.message
      const errorMessage =
        error?.response?.status === 409
          ? 'This application has already been submitted to TalentBridge. Duplicate applications are not allowed.'
          : Array.isArray(detailMessages)
          ? detailMessages.join(', ')
          : detailMessages ||
            responseData?.message ||
            responseData?.error ||
            'Failed to send lead to TalentBridge'

      setFormikErrors([errorMessage])
      setErrorsModalOpen(true)
    } finally {
      setIsSendingToTalentBridge(false)
    }
  }

  // Create a button element with tooltip when disabled due to ATS capabilities
  const renderAtsButton = () => {
    // Only show button for admin role
    if (role !== 'admin') {
      return undefined
    }

    // Handle both data formats
    const leadData = fetchedLeadDetails?.metaLeads?.[0] || fetchedLeadDetails
    const isLeadSynced = leadData?.status === 'synced'
    return {
      label: isLeadSynced ? 'Already Synced to ATS' : 'Push to ATS',
      onClick: handleSendToTalentBridge,
      disabled: isSendingToTalentBridge || atsCapabilityDisabled || isLeadSynced,
      color: (isLeadSynced ? 'success' : 'secondary') as
        | 'error'
        | 'success'
        | 'secondary'
        | 'primary'
        | 'info'
        | 'warning',
    }
  }

  // Get tooltip text if button should be disabled due to ATS capability
  const getAtsTooltipText = () => {
    // Handle both data formats
    const leadData = fetchedLeadDetails?.metaLeads?.[0] || fetchedLeadDetails
    const campaign = leadData?.campaign || fetchedLeadDetails?.campaign
    const isLeadSynced = leadData?.status === 'synced'
    if (isLeadSynced) {
      return 'This lead has already been synced to the ATS'
    }
    if (atsCapabilityDisabled) {
      const atsType = campaign?.jobs?.[0]?.atsAuthConfig?.type
      const atsName = atsType && atsProviders[atsType] ? atsProviders[atsType].name : atsType
      return `${atsName} doesn't have the push capability`
    }
    return ''
  }

  // Create
  useEffect(() => {
    // Get campaign questions
    axiosInstance
      .get('/campaign', {
        params: { id: campaignId },
      })
      .then((res) => {
        const { id, campaignTitle, questions } = res.data

        setCampaignData({ id, campaignTitle, questions })
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e)
      })
  }, [campaignId])

  // Check if ATS has push capability
  useEffect(() => {
    if (fetchedLeadDetails) {
      // Handle both data formats: wrapped in metaLeads array or flat structure
      const leadData = fetchedLeadDetails.metaLeads?.[0] || fetchedLeadDetails
      const campaign = leadData?.campaign || fetchedLeadDetails.campaign

      // Get the job and its associated ATS type
      const jobs = campaign?.jobs || []
      if (jobs.length > 0) {
        const job = jobs[0]
        const atsType = job.atsAuthConfig?.type
        // If there's an associated ATS, check its capabilities
        if (atsType && atsProviders[atsType]) {
          // Check if ATS supports applicants:push
          const hasPushCapability = atsProviders[atsType].capabilities.includes('applicants:push')
          setAtsCapabilityDisabled(!hasPushCapability)
        }
      }

      // Extract field data - handle both metaLeads wrapped and flat structure
      const fieldDataString = leadData?.fieldData || fetchedLeadDetails.fieldData
      if (fieldDataString) {
        let fullName = ''
        let firstName = ''
        let lastName = ''
        let phoneNumber = ''
        let email = ''
        const questions: any = {}
        parseFieldData(fieldDataString).forEach((item) => {
          const value = firstFieldValue(item)
          switch (classifyLeadField(item.name)) {
            case 'full_name':
              fullName = fullName || value
              break
            case 'first_name':
              firstName = firstName || value
              break
            case 'last_name':
              lastName = lastName || value
              break
            case 'phone_number':
              phoneNumber = phoneNumber || value
              break
            case 'email':
              email = email || value
              break
            default:
              questions[item.name] = value
          }
        })
        fullName = fullName || `${firstName} ${lastName}`.trim()

        setPhoneNumber(phoneNumber)
        setEmail(email)
        setQuestions(questions)
        setTitle(fullName)
      }

      // Handle files - check both structures
      const metaLeadFiles = leadData?.metaLeadFiles || fetchedLeadDetails.metaLeadFiles
      if (metaLeadFiles?.length) {
        const formattedFiles = metaLeadFiles.map((i: TgraphQlFile) => ({
          id: i?.id,
          url: fixImgSrc(i.file.url),
          name: i.name,
        }))
        setDataSources(formattedFiles)
      }
    }
  }, [fetchedLeadDetails])

  useEffect(() => {
    if (mutationError) {
      setFormikErrors(mutationError.graphQLErrors.map((i) => i.message))
      setErrorsModalOpen(true)
    }
    if (mutationErrorCreate) {
      setFormikErrors(mutationErrorCreate.graphQLErrors.map((i) => i.message))
      setErrorsModalOpen(true)
    }
    if (mutationErrorConnectCampaignWithLead) {
      setFormikErrors(mutationErrorConnectCampaignWithLead.graphQLErrors.map((i) => i.message))
      setErrorsModalOpen(true)
    }
  }, [mutationError, mutationErrorCreate, mutationErrorConnectCampaignWithLead])

  useEffect(() => {
    return () => {
      setPhoneNumber('')
      setEmail('')
      setQuestions([])
      setTitle('')
    }
  }, [])

  return (
    <DialogBox
      open={open}
      title={isUpdate ? shownTitle : 'Create Lead'}
      setOpen={() => handleCloseCB()}
      handleSubmit={() => handleSubmit()}
      isLoading={loadingLeadDetails || loadingMutation || loadingCreateLead || successModalOpen}
      additionalAction={isUpdate ? renderAtsButton() : undefined}
      additionalActionTooltip={getAtsTooltipText()}
    >
      {loadingLeadDetails || loadingMutation || loadingCreateLead ? (
        <Loading />
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} container rowSpacing={2}>
              {permission('status') && (
                <Grid item xs={12} container rowSpacing={2} columnSpacing={2} width={'100%'}>
                  <Grid item xs={isUpdate ? 12 : 6}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">Status</InputLabel>
                      <Select
                        labelId="demo-select-small-label"
                        id="status"
                        name="status"
                        value={formik.values.status || ''}
                        onChange={(e) => {
                          setStatus(e.target.value)
                          formik.setFieldValue('status', e.target.value)
                        }}
                        label={intl.formatMessage({ id: 'status' })}
                        style={{ width: '100%' }}
                      >
                        {leadStatuses.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    {!isUpdate && (
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Platform</InputLabel>
                        <Select
                          labelId="demo-select-small-label"
                          id="status"
                          name="status"
                          value={platform}
                          onChange={(e) => setPlatform(e.target.value)}
                          label={intl.formatMessage({ id: 'platform' })}
                          style={{ width: '100%' }}
                        >
                          {[
                            { name: 'Facebook', value: 'fb' },
                            { name: 'Instagram', value: 'ig' },
                            { name: 'Audience Network', value: 'an' },
                            { name: 'Messenger', value: 'msg' },
                          ].map((channel) => (
                            <MenuItem key={channel.name} value={channel.value}>
                              {channel.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                </Grid>
              )}
              {!isUpdate && permission('name') && (
                <Grid item xs={12}>
                  <Tooltip title={fullName} placement="top" enterDelay={500}>
                    <TextField
                      label={intl.formatMessage({ id: 'full-name' })}
                      placeholder="Full Name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      variant="outlined"
                      name="fullName"
                      fullWidth
                      multiline={fullName?.length > 55}
                      InputLabelProps={{
                        style: {
                          whiteSpace: 'normal',
                        },
                      }}
                      inputProps={{
                        style: {
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        },
                      }}
                    />
                  </Tooltip>
                </Grid>
              )}
              <Grid item container spacing={2} xs={12}>
                {permission('phone') && (
                  <Grid item xs={12}>
                    <Tooltip title={shownPhone} placement="top" enterDelay={500}>
                      <TextField
                        label={intl.formatMessage({ id: 'phone' })}
                        placeholder="Telefon"
                        value={shownPhone}
                        InputProps={{ readOnly: isMasked }}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        variant="outlined"
                        fullWidth
                        multiline={phoneNumber?.length > 55}
                        InputLabelProps={{
                          style: {
                            whiteSpace: 'normal',
                          },
                        }}
                        inputProps={{
                          style: {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          },
                        }}
                      />
                    </Tooltip>
                  </Grid>
                )}
                {permission('email') && (
                  <Grid item xs={12}>
                    <Tooltip title={shownEmail} placement="top" enterDelay={500}>
                      <TextField
                        label={intl.formatMessage({ id: 'email' })}
                        placeholder="Email"
                        value={shownEmail}
                        InputProps={{ readOnly: isMasked }}
                        onChange={(e) => setEmail(e.target.value)}
                        variant="outlined"
                        fullWidth
                        multiline={email?.length > 55}
                        InputLabelProps={{
                          style: {
                            whiteSpace: 'normal',
                          },
                        }}
                        inputProps={{
                          style: {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          },
                        }}
                      />
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
              <RenderQuestions />
            </Grid>

            <Grid item xs={6} className={classes.wrapper}>
              {showAtsNote && <AtsPushStatusNote {...atsPush} confirmedNow={confirmedNow} />}
              {permission('metaLeadFiles') && (
                <>
                  <Typography variant="h4">Files</Typography>
                  <MultiImageEdit
                    title="Files"
                    setFiles={setFiles}
                    setDataSources={setDataSources}
                    dataSources={dataSources}
                    idsToCreate={idsToCreate}
                    idsToDelete={idsToDelete}
                    setIdsToDelete={setIdsToDelete}
                    setIdsToCreate={setIdsToCreate}
                    hasPreview={false}
                    isMultiType
                    showButton={false}
                  />
                </>
              )}
              {<Timeline data={[]} />}
            </Grid>
          </Grid>
        </>
      )}

      <FormErrorsModal
        errorsModalOpen={errorsModalOpen}
        formikErrors={formikErrors}
        setErrorsModalOpen={setErrorsModalOpen}
      />
      <SuccessModal
        successModalOpen={successModalOpen}
        handleClose={() => handleCloseSuccess()}
        setSuccessModalOpen={setSuccessModalOpen}
        message={isUpdate ? 'Edited successfully' : 'Created successfully'}
      />
      <SuccessModal
        successModalOpen={talentBridgeSuccessModalOpen}
        handleClose={() => setTalentBridgeSuccessModalOpen(false)}
        setSuccessModalOpen={setTalentBridgeSuccessModalOpen}
        message="Lead sent to TalentBridge successfully"
      />
    </DialogBox>
  )
}

export default ReadEditLead
